import { GREY_600, GREY_700 } from "@kaggle/material";
import { ThemeName } from "@kaggle/styled-components";
import { createSelector } from "reselect";
import { EditorStatus } from "../../types/EditorStatus";
import {
  getCurrentEditor,
  getCurrentEditorStatus,
  getCurrentKernelRunId,
} from "./current";
import { getThemeName } from "./theme";

const INTERACTABLE_STATUSES = [
  EditorStatus.RUNNING,
  EditorStatus.ERROR,
  EditorStatus.STARTING,
];

/**
 * Selects value for whether the Jupyter frame is ready.
 * Returns true if editor is ready to handle interactions.
 *
 * Some examples of interactions are:
 *      - Read content from editor.
 *      - Write content to editor.
 */
export const getIsEditorInteractable = createSelector(
  [getCurrentEditorStatus],
  status => INTERACTABLE_STATUSES.includes(status)
);

/** Selects value for controlling actions that require Jupyter server. */
export const getIsEditorConnected = createSelector(
  [getCurrentEditorStatus],
  status => status !== EditorStatus.QUEUED
);

// TODO(cochamberlain): Should this include a check on status?
/** Selects whether the kernel is currently disconnected. */
export const getIsEditorDisconnected = createSelector(
  [getCurrentKernelRunId],
  runId => runId === null
);

/** Selects whether the notebook is in a loading state. */
export const getIsEditorQueued = createSelector(
  [getCurrentEditorStatus],
  status => status === EditorStatus.QUEUED
);

// TODO(cochamberlain): Should this include a check on status?
/** Selects whether the notebook is currently running. */
export const getIsKernelRunning = createSelector(
  [getCurrentEditor],
  editor => editor.isKernelRunning
);

/** Gets the current editor error message or null if none exists. */
export const getEditorErrorMessage = createSelector(
  [getCurrentEditor],
  editor => editor.error || null
);

/** Selects text to display in the editor for the current editor status. */
export const getEditorStatusTitle = createSelector(
  [getCurrentEditorStatus],
  status => convertEditorStatusToHumanReadable(status)
);

export const convertEditorStatusToHumanReadable = (status: EditorStatus) => {
  switch (status) {
    case EditorStatus.QUEUED:
      return "Loading";
    case EditorStatus.STARTING:
      return "Starting";
    case EditorStatus.RUNNING:
      return "Running";
    case EditorStatus.STOPPED:
      return "off (run a cell to start)";
    case EditorStatus.ERROR:
      return "Error";
    case EditorStatus.FATAL:
      return "Fatal";
  }
};

/** Selects text / icon color to display in the editor for the current editor status. */
export const getEditorStatusColor = createSelector(
  [getCurrentEditorStatus, getThemeName],
  (status, themeName) => getEditorStatusColorFromStatus(status, themeName)
);

export const getEditorStatusColorFromStatus = (
  status: EditorStatus,
  themeName: ThemeName
) => {
  switch (status) {
    case EditorStatus.QUEUED:
      return "#F9CF51";
    case EditorStatus.STARTING:
      return "#FDAA5C";
    case EditorStatus.RUNNING:
      return "#42E3BB";
    case EditorStatus.STOPPED:
      return themeName === "dark" ? GREY_700 : GREY_600;
    case EditorStatus.ERROR:
    case EditorStatus.FATAL:
      return "#F58B8A";
    default:
      return "#FDAA5C";
  }
};
